import React, { useState, useCallback, useEffect } from 'react';
import { RTVIEvent } from "@pipecat-ai/client-js";
import { usePipecatClient, useRTVIClientEvent } from "@pipecat-ai/client-react";
import StartVideoButton from './StartVideoButton';

const SidePanel = ({ onStartVideoChat, onConnectionError, onRetryConnection, translations, botConfig = "olivio_sales", botInfo = null }) => {
  const tr = translations || {}; // Fallback to empty object if translations not provided
  const client = usePipecatClient();
  const [isConnected, setIsConnected] = useState(false);
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);

  // Get persona name from botInfo or fallback to "Olivio"
  const personaName = botInfo?.personaName || "Olivio";
  const [showMicrophoneDropdown, setShowMicrophoneDropdown] = useState(false);

  // Sales bot state
  const [userInfo, setUserInfo] = useState({
    name: '',
    industry: '',
    company: '',
    flowStage: ''
  });

  const [appointmentInfo, setAppointmentInfo] = useState({
    date: '',
    time: '',
    email: '',
    flowStage: ''
  });

  const [appointmentConfirmed, setAppointmentConfirmed] = useState({
    confirmed: '',
    flowStage: ''
  });

  // Healthcare bot state
  const [healthcareInfo, setHealthcareInfo] = useState({
    patientName: '',
    healthConcern: '',
    urgency: '',
    appointmentScheduled: false
  });

  // Support bot state
  const [supportInfo, setSupportInfo] = useState({
    customerName: '',
    issueType: '',
    issueDescription: '',
    solutionProvided: false
  });

  // Concierge bot state
  const [conciergeInfo, setConciergeInfo] = useState({
    guestName: '',
    serviceType: '',
    reservationType: '',
    reservationConfirmed: false
  });

  // Get available microphones on component mount
  useEffect(() => {
    const getMicrophones = async () => {
      try {
        // Request microphone permission first
        await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Get all audio input devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(device => device.kind === 'audioinput');
        
        setAvailableMicrophones(microphones);
        
        // Set default microphone (usually the first one or default)
        const defaultMic = microphones.find(mic => mic.deviceId === 'default') || microphones[0];
        if (defaultMic) {
          setSelectedMicrophone(defaultMic);
        }
        
      } catch (error) {
        console.error('Error getting microphones:', error);
        // Set a fallback option if permission denied
        setAvailableMicrophones([{ deviceId: 'default', label: 'Default Microphone' }]);
        setSelectedMicrophone({ deviceId: 'default', label: 'Default Microphone' });
      }
    };

    getMicrophones();
  }, []);

  // Listen for connection events
  useRTVIClientEvent(
    RTVIEvent.Connected,
    useCallback(() => {
      console.log('Client connected');
      setIsConnected(true);
    }, [])
  );

  useRTVIClientEvent(
    RTVIEvent.BotReady,
    useCallback(() => {
      console.log('Bot ready');
      setIsConnected(true);
    }, [])
  );

  // Listen for server messages from your Pipecat bot
  useRTVIClientEvent(
    RTVIEvent.ServerMessage,
    useCallback((message) => {
      console.log('Received server message:', message);

      // Sales bot messages
      if (message.type === 'user_info_collected') {
        setUserInfo({
          name: message.user_data?.name || '',
          industry: message.user_data?.industry || '',
          company: message.user_data?.company || '',
          flowStage: message.flow_stage || ''
        });
      }

      if (message.type === 'appointment_info_collected') {
        setAppointmentInfo({
          date: message.appointment_data?.date || '',
          time: message.appointment_data?.time || '',
          email: message.appointment_data?.email || '',
          flowStage: message.flow_stage || ''
        });
      }

      if (message.type === 'appointment_confirmed') {
        setAppointmentConfirmed({
          confirmed: message.appointment_confirmed?.confirmed || '',
          flowStage: message.flow_stage || ''
        });
      }

      // Healthcare bot messages
      if (message.type === 'health_concern_identified') {
        setHealthcareInfo(prev => ({
          ...prev,
          healthConcern: message.concern_data?.health_concern || '',
          urgency: message.concern_data?.urgency || ''
        }));
      }

      if (message.type === 'appointment_scheduled') {
        setHealthcareInfo(prev => ({
          ...prev,
          appointmentScheduled: true
        }));
        setAppointmentInfo({
          date: message.appointment_data?.date || '',
          time: message.appointment_data?.time || '',
          email: message.appointment_data?.email || '',
          flowStage: 'appointment_scheduled'
        });
      }

      if (message.type === 'EMERGENCY') {
        // Show emergency alert
        console.error('EMERGENCY ALERT:', message.patient_data);
        setHealthcareInfo(prev => ({
          ...prev,
          urgency: 'EMERGENCY'
        }));
      }

      // Support bot messages
      if (message.type === 'issue_identified') {
        setSupportInfo({
          customerName: '',
          issueType: message.issue_data?.type || '',
          issueDescription: message.issue_data?.description || '',
          solutionProvided: false
        });
      }

      if (message.type === 'solution_provided') {
        setSupportInfo(prev => ({
          ...prev,
          solutionProvided: true
        }));
      }

      // Concierge bot messages
      if (message.type === 'recommendation_provided') {
        setConciergeInfo(prev => ({
          ...prev,
          serviceType: message.service_type || ''
        }));
      }

      if (message.type === 'reservation_made') {
        setConciergeInfo(prev => ({
          ...prev,
          reservationType: message.reservation_data?.type || '',
          reservationConfirmed: true
        }));
      }
    }, [])
  );




  const InfoLine = ({ label, value, delay = 0 }) => (
    <p 
      className="info-line" 
      style={{ 
        animationDelay: `${delay}ms`,
        fontSize: '1.1rem',
        margin: '8px 0',
        opacity: 0,
        animation: 'slideInLeft 0.6s ease-out forwards',
        color: "black",
      }}
    >
      <span className="green-tick">✓</span>
      <strong>{label}:</strong> {value}
    </p>
  );

  const clearAllInfo = useCallback(() => {
    // Clear sales bot state
    setUserInfo({
      name: '',
      industry: '',
      company: '',
      flowStage: ''
    });
    setAppointmentInfo({
      date: '',
      time: '',
      email: '',
      flowStage: ''
    });
    setAppointmentConfirmed({
      confirmed: '',
      flowStage: ''
    });

    // Clear healthcare bot state
    setHealthcareInfo({
      patientName: '',
      healthConcern: '',
      urgency: '',
      appointmentScheduled: false
    });

    // Clear support bot state
    setSupportInfo({
      customerName: '',
      issueType: '',
      issueDescription: '',
      solutionProvided: false
    });

    // Clear concierge bot state
    setConciergeInfo({
      guestName: '',
      serviceType: '',
      reservationType: '',
      reservationConfirmed: false
    });
  }, []);

  // Listen for disconnect events to clear data and update connection state
  useRTVIClientEvent(
    RTVIEvent.Disconnected,
    useCallback(() => {
      console.log('Client disconnected, clearing collected information');
      setIsConnected(false);
      clearAllInfo();
    }, [clearAllInfo])
  );
  
  // Listen for bot disconnect events to clear data and update connection state
  useRTVIClientEvent(
    RTVIEvent.BotDisconnected,
    useCallback(() => {
       try {
      if (client) {
         client.disconnect();
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
      console.log('Bot disconnected, clearing collected information');
      setIsConnected(false);
      clearAllInfo();
    }, [client,clearAllInfo])
  );

  const handleDisconnect = useCallback(async () => {
    try {
      if (client) {
        await client.disconnect();
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  }, [client]);

  const handleMicrophoneSelect = async (microphone) => {
    try {
      // Request permission for the specific microphone device
      const constraints = {
        audio: {
          deviceId: microphone.deviceId ? { exact: microphone.deviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };
      
      // This will trigger browser permission request for the specific microphone
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Stop the stream immediately as we're just checking permissions
      stream.getTracks().forEach(track => track.stop());
      
      // If successful, update the selected microphone
      setSelectedMicrophone(microphone);
      setShowMicrophoneDropdown(false);
      
      console.log('Microphone permission granted for:', microphone.label || microphone.deviceId);
      
    } catch (error) {
      console.error('Failed to get permission for microphone:', error);
      
      // Handle different error types
      if (error.name === 'NotAllowedError') {
        alert('Microphone access was denied. Please allow access and try again.');
      } else if (error.name === 'NotFoundError') {
        alert('Selected microphone not found. Please try a different microphone.');
      } else if (error.name === 'NotReadableError') {
        alert('Microphone is already in use by another application.');
      } else {
        alert('Error accessing microphone. Please try again.');
      }
      
      // Keep dropdown open so user can try again
    }
  };

  // Microphone Selector Component
  const MicrophoneSelector = () => (
    <div className="mic-selector-section">
      <div className="mic-selector-container">
        <button 
          className="mic-selector-button" 
          onClick={() => setShowMicrophoneDropdown(!showMicrophoneDropdown)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m12 1-3 3v4c0 1.657 1.343 3 3 3s3-1.343 3-3V4l-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dropdown-arrow">
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </button>
        
        <span className="mic-selector-label">
          {selectedMicrophone ?
            (selectedMicrophone.label || tr.defaultMicrophone || 'Default Microphone') :
            (tr.chooseMicrophone || 'Choose Microphone')
          }
        </span>

        {showMicrophoneDropdown && (
          <div className="mic-dropdown">
            {availableMicrophones.map((microphone, index) => (
              <button
                key={microphone.deviceId || index}
                className={`mic-dropdown-item ${selectedMicrophone?.deviceId === microphone.deviceId ? 'selected' : ''}`}
                onClick={() => handleMicrophoneSelect(microphone)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m12 1-3 3v4c0 1.657 1.343 3 3 3s3-1.343 3-3V4l-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
                <span>{microphone.label || `${tr.microphone || 'Microphone'} ${index + 1}`}</span>
                {selectedMicrophone?.deviceId === microphone.deviceId && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Disconnected View
  if (!isConnected) {
    return (
      <div className="popup-side-panel disconnected">
        <style jsx>{`
          .popup-side-panel.disconnected {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
            padding: 40px 20px 60px;
            background-color: rgba(255, 255, 255, 0.15);
          }

          .waiting-message {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 1.2rem;
            font-weight: 600;
            color: #f7f4f4ff;
            text-align: center;
            flex-shrink: 0;
            margin-bottom: 100px;
          }

          .checkdevice-message {
            font-size: 1.5rem;
            color: #f7f4f4ff;
            text-align: center;
            margin-bottom: 20px;
            padding: 0 10px;
            margin-top: 40px;
            font-weight: 700;
            
          }

          .green-dot {
            width: 12px;
            height: 12px;
            background-color: #28a745;
            border-radius: 50%;
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
            }
          }

          .start-video-button {
          
          background: #32c75c;
          }
          .start-video-button-container {
            width: 100%;
            max-width: 280px;
            display: flex;
            justify-content: center;
            margin-bottom: 40px;
            padding-top: 20px;
            
          }

          :global(.start-video-button-container > *) {
            width: 100% !important;
            min-height: 52px !important;
            padding: 14px 20px !important;
          }

          .mic-selector-section {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: auto;
            width: 100%;
            position: relative;
          }

          .mic-selector-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            position: relative;
            padding-bottom: 50px;
          }

          .mic-selector-button {
            position: relative;
            width: 56px;
            height: 56px;
            border-radius: 50%;
            border: none;
            background-color: white;
            color: #333;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            backdrop-filter: blur(10px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .mic-selector-button:hover {
            background-color: rgba(255, 255, 255, 0.9);
            transform: scale(1.05);
          }

          .dropdown-arrow {
            position: absolute;
            bottom: -2px;
            right: -8px;
            background: rgba(0, 0, 0, 0.6);
            border-radius: 50%;
            padding: 3px;
            width: 22px;
            height: 22px;
            color: white;
          }

          .mic-selector-label {
            font-size: 0.85rem;
            color: rgba(255, 255, 255, 0.9);
            text-align: center;
            font-weight: 500;
            max-width: 200px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          .mic-dropdown {
            position: absolute;
            top: 70px;
            left: 50%;
            transform: translateX(-50%);
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            padding: 8px;
            min-width: 250px;
            max-width: 300px;
            z-index: 1000;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .mic-dropdown-item {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;
            padding: 12px 16px;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 8px;
            font-size: 0.9rem;
            color: #333;
            transition: background-color 0.2s ease;
            text-align: left;
          }

          .mic-dropdown-item:hover {
            background-color: rgba(0, 123, 255, 0.1);
          }

          .mic-dropdown-item.selected {
            background-color: rgba(40, 167, 69, 0.1);
            color: #28a745;
            font-weight: 500;
          }

          .mic-dropdown-item span {
            flex: 1;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          /* Click outside to close dropdown */
          .mic-dropdown::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: -1;
          }
        `}</style>
        
        <div className="waiting-message">
          <span className="green-dot"></span>
          <span>{`${personaName} is waiting for you`}</span>
        </div>

        <div className="checkdevice-message">
          <span>{`Check your mic, so ${personaName} can hear you!`}</span>
        </div>

        <div className="start-video-button-container">
          <StartVideoButton
            onClick={onStartVideoChat}
            onConnectionError={onConnectionError}
            onRetryConnection={onRetryConnection}
            botConfig={botConfig}
          />
        </div>
        
        <MicrophoneSelector />
      </div>
    );
  }

  // Connected View - Client Intake Form
  return (
    <div className="popup-side-panel">
      <style jsx>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes checkmarkGrow {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .popup-side-panel {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          background-color: rgba(255, 255, 255, 0.95);
        }

        .side-panel-content {
          flex: 1;
          overflow-y: auto;
        }

        .side-panel-header {
          font-size: 1.2rem;
          font-weight: bold;
          margin-bottom: 20px;
          color: #333;
        }

        .panel-section {
          margin-bottom: 24px;
          padding: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          border-left: 4px solid #28a745;
        }

        .panel-section h4 {
          margin: 0 0 12px 0;
          color: #333;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .user-info {
          margin-top: 8px;
        }

        .info-line {
          display: flex;
          align-items: center;
          gap: 8px;
          line-height: 1.4;
          color: black;
        }

        .green-tick {
          color: #28a745;
          font-weight: bold;
          font-size: 1.2rem;
          margin-right: 4px;
        }

        .disconnect-button-container {
          padding: 20px;
          border-top: 1px solid #eee;
        }

        .disconnect-button {
          width: 100%;
          padding: 12px 16px;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .disconnect-button:hover {
          background-color: #c82333;
        }

        .disconnect-button:active {
          transform: translateY(1px);
        }

        /* Confirmation Overlay Styles */
        .confirmation-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(8px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 100;
          animation: fadeIn 0.5s ease-out forwards;
        }

        .confirmation-content {
          text-align: center;
          padding: 40px 20px;
          max-width: 300px;
        }

        .confirmation-checkmark {
          width: 80px;
          height: 80px;
          background-color: #28a745;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          animation: checkmarkGrow 0.6s ease-out 0.2s forwards;
          transform: scale(0);
        }

        .confirmation-checkmark svg {
          width: 40px;
          height: 40px;
          color: white;
          stroke-width: 3;
        }

        .confirmation-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #28a745;
          margin-bottom: 16px;
          animation: fadeIn 0.6s ease-out 0.4s forwards;
          opacity: 0;
        }

        .confirmation-subtitle {
          font-size: 1.1rem;
          color: #666;
          margin-bottom: 24px;
          animation: fadeIn 0.6s ease-out 0.6s forwards;
          opacity: 0;
        }

        .confirmation-details {
          background-color: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          border-left: 4px solid #28a745;
          animation: fadeIn 0.6s ease-out 0.8s forwards;
          opacity: 0;
        }

        .confirmation-detail-line {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 1.1rem;
          color: #333;
        }

        .confirmation-detail-line:last-child {
          margin-bottom: 0;
        }

        .confirmation-detail-icon {
          color: #28a745;
          font-size: 1.2rem;
        }

        .confirmation-detail-value {
          font-weight: 600;
        }
      `}</style>
      
      {/* Confirmation Overlay */}
      {appointmentConfirmed.confirmed && (
        <div className="confirmation-overlay">
          <div className="confirmation-content">
            <div className="confirmation-checkmark">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </div>
            
            <h2 className="confirmation-title">
              {tr.appointmentConfirmed || 'Your appointment is confirmed!'}
            </h2>

            <p className="confirmation-subtitle">
              {tr.lookForwardToMeeting || 'We look forward to meeting with you'}
            </p>
            
            <div className="confirmation-details">
              {appointmentInfo.date && (
                <div className="confirmation-detail-line">
                  <span className="confirmation-detail-icon">📅</span>
                  <span className="confirmation-detail-value">{appointmentInfo.date}</span>
                </div>
              )}
              {appointmentInfo.time && (
                <div className="confirmation-detail-line">
                  <span className="confirmation-detail-icon">🕐</span>
                  <span className="confirmation-detail-value">{appointmentInfo.time}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="side-panel-content">
        <div className="side-panel-header">{tr.clientIntakeForm || 'Client Intake Form'}</div>

        {/* User Information Section */}
        {userInfo.name && (
          <div className="panel-section">
            <h4><i className="fas fa-user"></i> {tr.userInformation || 'User Information'}</h4>
            <div className="user-info">
              <InfoLine label={tr.nameLabel || 'Name'} value={userInfo.name} delay={0} />
              <InfoLine label={tr.companyLabel || 'Company'} value={userInfo.company} delay={150} />
              <InfoLine label={tr.industryLabel || 'Industry'} value={userInfo.industry} delay={300} />
              {userInfo.flowStage && (
                <InfoLine
                  label={tr.stageLabel || 'Stage'}
                  value={userInfo.flowStage.replace('_', ' ')}
                  delay={450}
                />
              )}
            </div>
          </div>
        )}

        {/* Appointment Information Section */}
        {appointmentInfo.date && (
          <div className="panel-section">
            <h4><i className="fas fa-calendar"></i> {tr.appointmentInformation || 'Appointment Information'}</h4>
            <div className="user-info">
              <InfoLine label={tr.dateLabel || 'Date'} value={appointmentInfo.date} delay={0} />
              <InfoLine label={tr.timeLabel || 'Time'} value={appointmentInfo.time} delay={150} />
              <InfoLine label={tr.emailLabel || 'Email'} value={appointmentInfo.email} delay={300} />
              {appointmentInfo.flowStage && (
                <InfoLine
                  label={tr.stageLabel || 'Stage'}
                  value={appointmentInfo.flowStage.replace('_', ' ')}
                  delay={450}
                />
              )}
            </div>
          </div>
        )}

        {/* Healthcare Bot Information */}
        {healthcareInfo.healthConcern && (
          <div className="panel-section">
            <h4><i className="fas fa-heartbeat"></i> Health Concern</h4>
            <div className="user-info">
              <InfoLine label="Health Concern" value={healthcareInfo.healthConcern} delay={0} />
              <InfoLine label="Urgency" value={healthcareInfo.urgency} delay={150} />
              {healthcareInfo.urgency === 'EMERGENCY' && (
                <p style={{ color: 'red', fontWeight: 'bold', marginTop: '10px' }}>
                  🚨 EMERGENCY - Patient directed to call 911
                </p>
              )}
              {healthcareInfo.appointmentScheduled && (
                <InfoLine label="Status" value="Appointment Scheduled" delay={300} />
              )}
            </div>
          </div>
        )}

        {/* Support Bot Information */}
        {supportInfo.issueType && (
          <div className="panel-section">
            <h4><i className="fas fa-headset"></i> Support Issue</h4>
            <div className="user-info">
              <InfoLine label="Issue Type" value={supportInfo.issueType} delay={0} />
              <InfoLine label="Description" value={supportInfo.issueDescription} delay={150} />
              {supportInfo.solutionProvided && (
                <InfoLine label="Status" value="Solution Provided" delay={300} />
              )}
            </div>
          </div>
        )}

        {/* Concierge Bot Information */}
        {conciergeInfo.serviceType && (
          <div className="panel-section">
            <h4><i className="fas fa-concierge-bell"></i> Guest Services</h4>
            <div className="user-info">
              <InfoLine label="Service Type" value={conciergeInfo.serviceType} delay={0} />
              {conciergeInfo.reservationType && (
                <InfoLine label="Reservation" value={conciergeInfo.reservationType} delay={150} />
              )}
              {conciergeInfo.reservationConfirmed && (
                <InfoLine label="Status" value="Reservation Confirmed" delay={300} />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="disconnect-button-container">
        <button className="disconnect-button" onClick={handleDisconnect}>
          {tr.disconnect || 'Disconnect'}
        </button>
      </div>
    </div>
  );
};

export default SidePanel;